import { Fragment, useEffect, useState } from 'react';
import { useSpacePlanningStore } from '@/store/spacePlanningStore';
import { ParsedData, RoomData } from './SpacePlanningTypes';
import { newModal, WeaveModalElement } from '@/components/UI/WeaveModal/WeaveModal';
import { WeaveButton } from '@/components/UI/WeaveButton/WeaveButton';
import { WeaveRadioButtonGroup } from '@/components/UI/WeaveRadioButton/WeaveRadioButtonGroup';
import { WeaveRadioButton } from '@/components/UI/WeaveRadioButton/WeaveRadioButton';
import { unitConversionService as unitService, Unit } from '@/services/unit-conversion.service';
import styles from './styles.module.scss';

interface RoomImportProps {
	data: ParsedData[];
	path: string;
}

const RoomsTable = ({ data, path }: { data: ParsedData[]; path: string }) => {
	const { selectedRoom, confirmButton, roomsTable } = styles;

	const [selectedRooms, setSelectedRooms] = useState<RoomData[]>([]);
	const [unit, setUnit] = useState(() => {
		const preferred = unitService.getPreferredUnit('area') ?? 'm2';
		return preferred;
	});

	const { addRooms } = useSpacePlanningStore();

	const handleRoomClick = (room: RoomData) => {
		setSelectedRooms((prevSelected) => {
			const isSelected = prevSelected.some((r) => JSON.stringify(r) === JSON.stringify(room));
			if (isSelected) {
				return prevSelected.filter((r) => JSON.stringify(r) !== JSON.stringify(room));
			} else {
				return [...prevSelected, room];
			}
		});
	};

	const isRoomSelected = (room: RoomData) => {
		return selectedRooms.some((r) => JSON.stringify(r) === JSON.stringify(room));
	};

	const handleUnitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setUnit(e.target.value as Unit);
	};

	const handleAddRooms = () => {
		const convertedRooms =
			unit === 'ft2'
				? selectedRooms.map((room) => ({
						...room,
						ASF: unitService.convert(room.ASF, 'ft2', 'm2', 2),
						totalASF: unitService.convert(room.totalASF, 'ft2', 'm2', 2),
					}))
				: selectedRooms;

		addRooms(path, convertedRooms);
		setSelectedRooms([]);

		const existingModal = document.querySelector('weave-modal') as WeaveModalElement | null;
		if (existingModal) {
			existingModal.close();
		}
	};

	return (
		<div>
			<table className={roomsTable}>
				<thead>
					<tr>
						<th>Space/Description</th>
						<th>Space #</th>
						<th>Occupancy</th>
						<th>Quantity</th>
						<th>ASF</th>
						<th>Total ASF</th>
					</tr>
				</thead>
				<tbody>
					{data.map((item, index) => (
						<Fragment key={index}>
							<tr>
								<td
									colSpan={6}
									style={{ fontWeight: 'bold', backgroundColor: '#f0f0f0' }}
								>
									{item.space.header.spaceDescription} -{' '}
									{item.space.header.totalASF} ASF
								</td>
							</tr>
							<tr>
								<td
									colSpan={6}
									style={{ fontStyle: 'italic', backgroundColor: '#fafafa' }}
								>
									{item.space.sub.header.spaceDescription} -{' '}
									{item.space.sub.header.totalASF} ASF
								</td>
							</tr>
							{item.space.sub.data.map((room, roomIndex) => (
								<tr
									key={roomIndex}
									onClick={() => handleRoomClick(room)}
									style={{ cursor: 'pointer' }}
									className={isRoomSelected(room) ? selectedRoom : ''}
								>
									<td>{room.spaceDescription}</td>
									<td>{room.spaceId}</td>
									<td>{room.occupancy}</td>
									<td>{room.quantity}</td>
									<td>{room.ASF}</td>
									<td>{room.totalASF}</td>
								</tr>
							))}
						</Fragment>
					))}
				</tbody>
			</table>

			{selectedRooms.length > 0 && (
				<div className={styles.footer}>
					<div className={styles.leftSection}>
						<p>
							Choose the <strong>unit</strong> that corresponds to the import file:
						</p>
						<WeaveRadioButtonGroup name="unit-group">
							<WeaveRadioButton value="m2">
								<label className="radio-button">
									<input
										type="radio"
										value="m2"
										name="unit"
										checked={unit === 'm2'}
										onChange={handleUnitChange}
									/>
									m2
								</label>
							</WeaveRadioButton>
							<WeaveRadioButton value="ft2">
								<label className="radio-button">
									<input
										type="radio"
										value="ft2"
										name="unit"
										checked={unit === 'ft2'}
										onChange={handleUnitChange}
									/>
									ft2
								</label>
							</WeaveRadioButton>
						</WeaveRadioButtonGroup>
					</div>

					<div className={styles.rightSection}>
						<WeaveButton
							className={confirmButton}
							variant="solid"
							onClick={handleAddRooms}
						>
							Import ({selectedRooms.length}{' '}
							{selectedRooms.length > 1 ? 'rooms' : 'room'})
						</WeaveButton>
					</div>
				</div>
			)}
		</div>
	);
};

export const RoomImport = ({ data, path }: RoomImportProps) => {
	useEffect(() => {
		if (data.length === 0) return;
		newModal({
			width: '100%',
			title: 'Imported Room Data',
			content: <RoomsTable data={data} path={path} />,
		});
	}, [data, path]);

	if (data.length === 0) {
		return null;
	}
	return <></>;
};
